import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getDefaultPeriodRange } from '@/utils/dashboardUtils'
import {
  getDashboardCountries,
  getDashboardEntities,
  getDashboardCurrencies,
  getDashboardValueConversions,
} from '@/api/dashboardApi'

// The default filter shape shared by the current selection and the applied set.
const defaultFilters = () => ({
  countries: [],
  entityIds: [],
  fromMonth: getDefaultPeriodRange().fromMonth,
  fromYear: getDefaultPeriodRange().fromYear,
  toMonth: getDefaultPeriodRange().toMonth,
  toYear: getDefaultPeriodRange().toYear,
  currencyCode: 'USD',
  valueConversion: 'millions',
})

const initialState = {
  // Current selection — mirrors the in-popover draft and drives the
  // country-dependent entity refetch. Does NOT drive the dashboard fetch.
  ...defaultFilters(),
  // Committed filters the dashboard reads/fetches on. Updated ONLY on
  // Apply / Reset, so transient country selection never reloads the dashboard.
  appliedFilters: defaultFilters(),
  // dropdown option lists (populated from the APIs)
  options: {
    countryOptions: [],
    entityOptions: [],
    currencyOptions: [],
    valueConversionOptions: [],
  },
  optionsLoading: false,
  optionsLoaded: false,
  // entity list is refetched whenever the country selection changes
  entitiesLoading: false,
  entitiesError: false,
}

// Extracts just the filter fields from a state slice (drops options/flags).
const pickFilters = source => ({
  countries: source.countries,
  entityIds: source.entityIds,
  fromMonth: source.fromMonth,
  fromYear: source.fromYear,
  toMonth: source.toMonth,
  toYear: source.toYear,
  currencyCode: source.currencyCode,
  valueConversion: source.valueConversion,
})

// Fetches the dropdown option lists. The entities API depends on the country
// codes, so countries must resolve first; entities is then requested with the
// full list of country codes. Currencies and value-conversions have no such
// dependency and are fetched in parallel alongside countries.
// The raw backend item arrays are stored as-is (no reshaping). Countries and
// entities default to "all selected" using the returned code / entityUuid.
export const fetchDashboardFilterOptions = createAsyncThunk(
  'dashboardFilter/fetchOptions',
  async () => {
    // Countries + independent lists run together.
    const [countries, currencies, valueConversions] = await Promise.all([
      getDashboardCountries(),
      getDashboardCurrencies(),
      getDashboardValueConversions(),
    ])
    // Countries/value-conversions return the raw { status, data: [] } envelope.
    // The backend can return `data: null` (e.g. no matching records), so every
    // list is coerced to an array to keep downstream `.map` calls safe.
    const countryOptions =
      countries?.success && countries?.data?.data ? countries.data.data : []
    const valueConversionOptions =
      valueConversions?.success && valueConversions?.data?.data
        ? valueConversions.data.data
        : []
    const currencyOptions =
      currencies?.success && currencies?.data?.data ? currencies.data.data : []
    // Entities depend on the country codes returned above. The initial request
    // sends every available country code.
    const countryCodes = countryOptions.map(item => item.countryCode)
    const entities = await getDashboardEntities(countryCodes)
    const entityOptions =
      entities?.success && entities?.data?.data ? entities.data.data : []
    return {
      countryOptions,
      entityOptions,
      currencyOptions,
      valueConversionOptions,
    }
  }
)

// Refetches only the entity list for the given country codes. Used when the
// user changes the country selection so the entity dropdown stays in sync.
// Returns the raw entity item array (coerced to [] when the backend sends null
// or the request fails).
export const fetchDashboardEntitiesByCountries = createAsyncThunk(
  'dashboardFilter/fetchEntitiesByCountries',
  async (countryCodes = []) => {
    if (!countryCodes.length) return []
    const entities = await getDashboardEntities(countryCodes)
    return entities?.success && entities?.data?.data ? entities.data.data : []
  }
)

// Reset — restore the "all countries" default and refetch the entity list for
// that full country set before committing the applied filters. This is a thunk
// (not a plain reducer) because the entity options list may have been wiped
// (e.g. the user cleared the country selection, which empties
// options.entityOptions). A synchronous reducer can only re-default the
// selection, so on its own it would leave the entity dropdown blank and commit
// an empty entityIds to appliedFilters — reloading the dashboard with no
// entities. Refetching here keeps the options list, the selection, and the
// committed appliedFilters consistent.
export const resetDashboardFilters = createAsyncThunk(
  'dashboardFilter/reset',
  async (_, { getState }) => {
    const { options } = getState().dashboardFilter
    const countryCodes = options.countryOptions.map(item => item.countryCode)
    if (!countryCodes.length) return []
    const entities = await getDashboardEntities(countryCodes)
    return entities?.success && entities?.data?.data ? entities.data.data : []
  }
)

const dashboardFilterSlice = createSlice({
  name: 'dashboardFilter',
  initialState,
  reducers: {
    setDashboardCountries: (state, action) => {
      state.countries = action.payload
    },
    setDashboardEntityIds: (state, action) => {
      state.entityIds = action.payload
    },
    setDashboardFromMonth: (state, action) => {
      state.fromMonth = action.payload
    },
    setDashboardFromYear: (state, action) => {
      state.fromYear = action.payload
    },
    setDashboardToMonth: (state, action) => {
      state.toMonth = action.payload
    },
    setDashboardToYear: (state, action) => {
      state.toYear = action.payload
    },
    setDashboardCurrencyCode: (state, action) => {
      state.currencyCode = action.payload
    },
    setDashboardValueConversion: (state, action) => {
      state.valueConversion = action.payload
    },
    // Apply — merge the draft into the current selection AND commit it to
    // appliedFilters (the dashboard watches appliedFilters and reloads).
    setDashboardFilters: (state, action) => {
      Object.assign(state, action.payload)
      state.appliedFilters = pickFilters(state)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchDashboardFilterOptions.pending, state => {
        state.optionsLoading = true
      })
      .addCase(fetchDashboardFilterOptions.fulfilled, (state, action) => {
        state.options = action.payload
        state.optionsLoading = false
        state.optionsLoaded = true
        // Initial value for countries & entities is "all values" from the API.
        state.countries = action.payload.countryOptions.map(
          item => item.countryCode
        )
        state.entityIds = action.payload.entityOptions.map(
          item => item.entityUuid
        )
        // Seed the committed filters so the dashboard's first fetch uses the
        // API-derived defaults.
        state.appliedFilters = pickFilters(state)
      })
      .addCase(fetchDashboardFilterOptions.rejected, state => {
        state.optionsLoading = false
      })
      // Country-driven entity refetch.
      .addCase(fetchDashboardEntitiesByCountries.pending, state => {
        state.entitiesLoading = true
        state.entitiesError = false
        // Clear the selection while the new list loads.
        state.entityIds = []
        state.options.entityOptions = []
      })
      .addCase(fetchDashboardEntitiesByCountries.fulfilled, (state, action) => {
        const entityOptions = action.payload
        state.options.entityOptions = entityOptions
        state.entitiesLoading = false
        state.entitiesError = false
        // Do NOT auto-select here. Auto-select-all is only the INITIAL default
        // (see fetchDashboardFilterOptions.fulfilled). Once the user changes
        // the country and the entity list is refetched, the entity selection
        // is left empty so the user picks entities explicitly.
        state.entityIds = []
      })
      .addCase(fetchDashboardEntitiesByCountries.rejected, state => {
        state.entitiesLoading = false
        state.entitiesError = true
        state.entityIds = []
        state.options.entityOptions = []
      })
      // Reset — re-default every filter to "all"/defaults. The entity list is
      // refetched by the thunk so a prior "no country selected" state can't
      // leave the entity options/selection blank.
      .addCase(resetDashboardFilters.pending, state => {
        const allCountries = state.options.countryOptions.map(
          item => item.countryCode
        )
        // Re-default all filters and restore the full country selection. Entity
        // options/selection are (re)loaded in the fulfilled case below.
        Object.assign(state, defaultFilters(), {
          countries: allCountries,
          entityIds: [],
        })
        state.options.entityOptions = []
        state.entitiesLoading = true
        state.entitiesError = false
      })
      .addCase(resetDashboardFilters.fulfilled, (state, action) => {
        const entityOptions = action.payload
        state.options.entityOptions = entityOptions
        state.entityIds = entityOptions.map(item => item.entityUuid)
        state.entitiesLoading = false
        state.entitiesError = false
        // Commit the fully-resolved reset (incl. the refetched entities) as the
        // applied filters so the dashboard reloads on the correct default set.
        state.appliedFilters = pickFilters(state)
      })
      .addCase(resetDashboardFilters.rejected, state => {
        state.entitiesLoading = false
        state.entitiesError = true
        state.entityIds = []
        state.options.entityOptions = []
        // Still commit the reset selection so the rest of the filters apply.
        state.appliedFilters = pickFilters(state)
      })
  },
})

export const {
  setDashboardCountries,
  setDashboardEntityIds,
  setDashboardFromMonth,
  setDashboardFromYear,
  setDashboardToMonth,
  setDashboardToYear,
  setDashboardCurrencyCode,
  setDashboardValueConversion,
  setDashboardFilters,
} = dashboardFilterSlice.actions

export default dashboardFilterSlice.reducer
